/**
 * =============================================================================
 * ActivitiesTable — Tabela reutilizável de atividades
 *
 * Componente extraído da página `ActivitiesList` para permitir 100% de reuso
 * entre a Listagem (página) e widgets do Dashboard "Minhas Atividades".
 *
 * Inclui:
 *   - Tabela desktop com 13 colunas (Status, Tipo, Título, Vence em, Origem,
 *     Negócio, Status do Negócio, Cliente, Vendedor, Workspace, Pipeline, Fase)
 *   - Cards mobile equivalentes
 *   - Suporte opcional a seleção em massa (checkbox por linha + select-all)
 *   - Skeleton + EmptyState
 *
 * RELEASE: 2026-05-14 v1.0.0 - Extração da página ActivitiesList sem alterar
 *                              comportamento ou aparência.
 * =============================================================================
 */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertTriangle,
  Briefcase,
  Building2,
  Calendar as CalendarIcon,
  CalendarCheck,
  CheckCircle2,
  CircleDot,
  Layers,
  ListChecks,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  SkipForward,
  Zap,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import { DealStatusBadge } from '@/components/crm/DealStatusBadge';
import { PhaseProgressCell } from '@/components/crm/deals/PhaseProgressCell';
import { formatCurrency } from '@/lib/currency-utils';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import { getDeadlineInfo, DEADLINE_PILL_STYLES } from '@/lib/deadline-utils';
import { cn } from '@/lib/utils';
import type {
  ActivityRow,
  ActivityStatus,
  ActivityOrigin,
} from '@/hooks/useActivitiesPaginated';

// =============================================================================
// Constantes auxiliares
// =============================================================================

/** Mapa de ícones por tipo de atividade. */
export const ACTIVITY_ICON: Record<string, typeof Phone> = {
  call: Phone,
  email: Mail,
  whatsapp: MessageSquare,
  meeting: CalendarIcon,
  task: ListChecks,
};

/** Status finais — atividades nestes estados são imutáveis (sem ações em massa). */
export const FINAL_ACTIVITY_STATUSES = new Set<string>([
  'pulada',
  'concluida',
  'cancelada',
]);

/** Helper: indica se a atividade ainda aceita ações (concluir, pular, etc). */
export const isActivityActionable = (status: string | null | undefined): boolean =>
  !!status && !FINAL_ACTIVITY_STATUSES.has(status);

/**
 * v1.1.0 — Helper: indica se a linha pode ser editada a partir da listagem.
 *
 * Regras (paridade com a aba Cadência do modal de Negócio):
 *   - Atividade avulsa (standalone) em status não-final → editável via
 *     `ActivityEditSheet`.
 *   - Step de cadência criado manualmente (`is_manual = true`) em status não
 *     -final → editável via `ManualCadenceStepDialog`.
 *   - Demais origens (bookings, steps automáticos, itens finalizados) não são
 *     editáveis.
 */
export const canEditActivityRow = (row: ActivityRow): boolean => {
  if (!isActivityActionable(row.status)) return false;
  if (row.origin === 'standalone') return true;
  if (row.origin === 'cadence' && row.is_manual) return true;
  return false;
};

// =============================================================================
// Props
// =============================================================================
export interface ActivitiesTableProps {
  rows: ActivityRow[];
  isLoading?: boolean;
  /** Mapa de seleção (key = `${origin}-${activity_id}`). */
  selected?: Record<string, { origin: ActivityOrigin; id: string }>;
  /** Quando true, exibe coluna/checkbox de seleção em massa. */
  bulkMode?: boolean;
  allSelected?: boolean;
  onToggleRow?: (row: ActivityRow) => void;
  onToggleAll?: () => void;
  onOpenDeal: (row: ActivityRow) => void;
  onOpenActivity: (row: ActivityRow) => void;
  /**
   * v1.1.0 — Callback opcional para o botão "Editar" da coluna Ações.
   * Só é acionado para linhas que satisfazem {@link canEditActivityRow}.
   * Quando ausente, a coluna Ações é omitida.
   */
  onEditRow?: (row: ActivityRow) => void;
  /** Conteúdo customizado quando vazio. */
  emptyState?: React.ReactNode;
  /**
   * Quando `true` (default), aplica `border rounded-md` no wrapper desktop.
   * Use `false` em widgets de dashboard onde o Card externo já fornece a borda.
   */
  bordered?: boolean;
}

// =============================================================================
// Componente principal
// =============================================================================
export function ActivitiesTable({
  rows,
  isLoading,
  selected = {},
  bulkMode = false,
  allSelected = false,
  onToggleRow,
  onToggleAll,
  onOpenDeal,
  onOpenActivity,
  onEditRow,
  emptyState,
  bordered = true,
}: ActivitiesTableProps) {
  if (isLoading) return <ActivitiesSkeleton />;
  if (rows.length === 0) return <>{emptyState ?? <EmptyState />}</>;

  return (
    <>
      {/* Desktop: tabela com scroll horizontal (padrão CrmDeals) + setinhas */}
      <ScrollIndicators
        className={cn('hidden md:block flex-1', bordered && 'border rounded-md')}
      >
        {/*
          Wrapper visual apenas: NÃO adicionar overflow-auto aqui para evitar
          viewport aninhado com o wrapper interno do <Table>. O viewport real
          de rolagem é o div interno criado pelo <Table scrollViewport />.
        */}
        <div className="h-full w-full overflow-hidden">
          <Table
            scrollViewport
            containerClassName="h-full"
            className="min-w-[2160px]"
          >
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {bulkMode && (
                  <TableHead className="w-10 px-2">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={onToggleAll}
                      aria-label="Selecionar todas"
                    />
                  </TableHead>
                )}
                <TableHead className="text-xs font-medium w-[120px]">Status</TableHead>
                <TableHead className="text-xs font-medium w-[80px]">Tipo</TableHead>
                <TableHead className="text-xs font-medium min-w-[260px]">Título</TableHead>
                <TableHead className="text-xs font-medium w-[150px]">Vence em</TableHead>
                <TableHead className="text-xs font-medium min-w-[180px]">Realizado por</TableHead>
                <TableHead className="text-xs font-medium min-w-[180px]">Origem / Step</TableHead>
                <TableHead className="text-xs font-medium min-w-[240px]">Negócio</TableHead>
                <TableHead className="text-xs font-medium w-[120px]">Status Negócio</TableHead>
                <TableHead className="text-xs font-medium min-w-[200px]">Cliente</TableHead>
                <TableHead className="text-xs font-medium min-w-[180px]">Vendedor</TableHead>
                <TableHead className="text-xs font-medium min-w-[140px]">Workspace</TableHead>
                <TableHead className="text-xs font-medium min-w-[140px]">Pipeline</TableHead>
                <TableHead className="text-xs font-medium min-w-[180px]">Fase</TableHead>
                {/* v2.2.0 — colunas de datas de criação e conclusão */}
                <TableHead className="text-xs font-medium w-[130px]">Criada em</TableHead>
                <TableHead className="text-xs font-medium w-[140px]">Concluída em</TableHead>
                {/* v1.1.0 — Coluna Ações (só aparece quando o caller injeta o handler) */}
                {onEditRow && (
                  <TableHead className="text-xs font-medium w-[80px] text-right">Ações</TableHead>
                )}
              </TableRow>

            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const key = `${row.origin}-${row.activity_id}`;
                return (
                  <ActivityRowDesktop
                    key={key}
                    row={row}
                    bulkMode={bulkMode}
                    selected={!!selected[key]}
                    onToggleSelect={() => onToggleRow?.(row)}
                    onOpenDeal={() => onOpenDeal(row)}
                    onOpenActivity={() => onOpenActivity(row)}
                    onEdit={onEditRow ? () => onEditRow(row) : undefined}
                  />
                );
              })}
            </TableBody>
          </Table>
        </div>
      </ScrollIndicators>

      {/* Mobile: cards */}
      <div className="md:hidden space-y-2">
        {rows.map((row) => {
          const key = `${row.origin}-${row.activity_id}`;
          return (
            <ActivityCardMobile
              key={key}
              row={row}
              bulkMode={bulkMode}
              selected={!!selected[key]}
              onToggleSelect={() => onToggleRow?.(row)}
              onOpenDeal={() => onOpenDeal(row)}
              onOpenActivity={() => onOpenActivity(row)}
            />
          );
        })}
      </div>
    </>
  );
}

// =============================================================================
// Linha desktop
// =============================================================================
function ActivityRowDesktop({
  row,
  bulkMode,
  selected,
  onToggleSelect,
  onOpenDeal,
  onOpenActivity,
  onEdit,
}: {
  row: ActivityRow;
  bulkMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onOpenDeal: () => void;
  onOpenActivity: () => void;
  /** v1.1.0 — Quando fornecido e a linha for editável, renderiza a coluna Ações. */
  onEdit?: () => void;
}) {
  const Icon = ACTIVITY_ICON[row.activity_type] ?? ListChecks;
  const dueLabel = row.due_at
    ? format(new Date(row.due_at), "dd/MM 'às' HH:mm", { locale: ptBR })
    : '—';
  // UUID truncado a 8 chars uppercase (padrão do projeto)
  // Bookings (agendamento online) podem não ter deal_id vinculado
  const dealCode = row.deal_id ? row.deal_id.slice(0, 8).toUpperCase() : null;
  const valueLabel =
    row.deal_value != null
      ? formatCurrency(Number(row.deal_value), { currency: row.deal_currency ?? 'BRL' })
      : null;
  const deadline = getDeadlineInfo(row.due_at, row.status);

  return (
    <TableRow className={cn('hover:bg-muted/30 transition-colors', selected && 'bg-primary/5')}>
      {bulkMode && (
        <TableCell className="px-2 py-2">
          <span title={!isActivityActionable(row.status) ? 'Atividade já finalizada — não permite ações em massa' : undefined}>
            <Checkbox
              checked={selected}
              onCheckedChange={onToggleSelect}
              disabled={!isActivityActionable(row.status)}
              aria-label="Selecionar atividade"
              className={!isActivityActionable(row.status) ? 'opacity-40 cursor-not-allowed' : undefined}
            />
          </span>
        </TableCell>
      )}
      <TableCell className="px-3 py-2"><StatusBadge status={row.status} /></TableCell>
      <TableCell className="px-3 py-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
      </TableCell>
      <TableCell className="px-3 py-2">
        <button
          type="button"
          onClick={onOpenActivity}
          className="text-left group min-w-0 max-w-full"
          title={row.title}
        >
          <div className="font-medium truncate max-w-[260px] group-hover:text-primary group-hover:underline">
            {row.title}
          </div>
          {row.is_required && (
            <span className="text-[10px] uppercase tracking-wide text-amber-600">
              Obrigatório
            </span>
          )}
        </button>
      </TableCell>
      <TableCell className="px-3 py-2 text-xs">
        <div className="flex flex-col gap-0.5">
          <span
            className={cn(
              'whitespace-nowrap',
              row.is_overdue && 'text-destructive font-medium',
            )}
          >
            {dueLabel}
          </span>
          {deadline && (
            <span
              className={cn(
                'inline-flex w-fit rounded-full px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap',
                DEADLINE_PILL_STYLES[deadline.variant],
              )}
            >
              {deadline.text}
            </span>
          )}
        </div>
      </TableCell>
      {/* "Realizado por" — executor real (performed_by). Mostra "—" quando ausente. */}
      <TableCell className="px-3 py-2">
        {row.performed_by_id || row.performed_by_name ? (
          <div className="flex items-center gap-2 min-w-0">
            <SmartAvatar
              src={row.performed_by_avatar ?? undefined}
              name={row.performed_by_name ?? '—'}
              size="xs"
              use3DFallback
            />
            <span className="text-xs text-muted-foreground truncate max-w-[140px]">
              {row.performed_by_name ?? '—'}
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="px-3 py-2">
        <OriginCell row={row} />
      </TableCell>
      <TableCell className="px-3 py-2">
        {row.deal_id ? (
          <button
            type="button"
            onClick={onOpenDeal}
            className="text-left group min-w-0 max-w-full"
            title={`${row.deal_title ?? ''} · ${dealCode ?? ''}`}
          >
            <div className="text-sm font-medium text-primary group-hover:underline truncate max-w-[240px]">
              {row.deal_title ?? dealCode}
            </div>
            {valueLabel && (
              <div className="text-[11px] text-muted-foreground">{valueLabel}</div>
            )}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="px-3 py-2">
        <DealStatusBadge status={row.deal_status} />
      </TableCell>
      {/* Cliente — padrão unificado: avatar (iniciais) + nome truncado */}
      <TableCell className="px-3 py-2">
        {row.customer_name ? (
          <div className="flex items-center gap-2 min-w-0">
            <SmartAvatar
              src={row.customer_avatar ?? undefined}
              name={row.customer_name}
              size="xs"
            />
            <div className="flex flex-col min-w-0">
              <span className="text-sm truncate max-w-[140px]">{row.customer_name}</span>
              {row.customer_phone && (
                <span className="text-[11px] text-muted-foreground truncate max-w-[140px]">
                  {formatPhoneForDisplay(row.customer_phone)}
                </span>
              )}
            </div>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </TableCell>
      <TableCell className="px-3 py-2">
        {row.assignee_id || row.assignee_name ? (
          <div className="flex items-center gap-2 min-w-0">
            <SmartAvatar
              src={row.assignee_avatar ?? undefined}
              name={row.assignee_name ?? '—'}
              size="xs"
              use3DFallback
            />
            <span className="text-xs text-muted-foreground truncate max-w-[140px]">
              {row.assignee_name ?? '—'}
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="px-3 py-2">
        {row.workspace_name ? (
          <div className="flex items-center gap-1.5 min-w-0">
            <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="text-xs text-muted-foreground truncate max-w-[140px]">
              {row.workspace_name}
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="px-3 py-2">
        {row.pipeline_name ? (
          <div className="flex items-center gap-1.5 min-w-0">
            <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="text-xs text-muted-foreground truncate max-w-[140px]">
              {row.pipeline_name}
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="px-3 py-2">
        {row.pipeline_id && row.phase_name && row.phase_sort_order != null ? (
          <PhaseProgressCell
            pipelineId={row.pipeline_id}
            phaseName={row.phase_name}
            phaseColor={row.phase_color}
            phaseSortOrder={row.phase_sort_order}
          />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      {/* v2.2.0 — Criada em */}
      <TableCell className="px-3 py-2 text-xs whitespace-nowrap text-muted-foreground">
        {row.created_at
          ? format(new Date(row.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })
          : '—'}
      </TableCell>
      {/* v2.2.0 — Concluída em */}
      <TableCell className="px-3 py-2 text-xs whitespace-nowrap text-muted-foreground">
        {row.completed_at
          ? format(new Date(row.completed_at), "dd/MM/yyyy HH:mm", { locale: ptBR })
          : '—'}
      </TableCell>
      {/* v1.1.0 — Ações (Editar) — só renderiza a célula quando o caller ativa a coluna */}
      {onEdit && (
        <TableCell className="px-3 py-2 text-right">
          {canEditActivityRow(row) ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              title="Editar atividade"
              aria-label="Editar atividade"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </TableCell>
      )}
    </TableRow>

  );
}

// =============================================================================
// Card mobile
// =============================================================================
function ActivityCardMobile({
  row,
  bulkMode,
  selected,
  onToggleSelect,
  onOpenDeal,
  onOpenActivity,
}: {
  row: ActivityRow;
  bulkMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onOpenDeal: () => void;
  onOpenActivity: () => void;
}) {
  const Icon = ACTIVITY_ICON[row.activity_type] ?? ListChecks;
  const dueLabel = row.due_at
    ? format(new Date(row.due_at), 'dd/MM HH:mm', { locale: ptBR })
    : '—';
  const dealCode = row.deal_id ? row.deal_id.slice(0, 8).toUpperCase() : null;
  const valueLabel =
    row.deal_value != null
      ? formatCurrency(Number(row.deal_value), { currency: row.deal_currency ?? 'BRL' })
      : null;
  const deadline = getDeadlineInfo(row.due_at, row.status);

  return (
    <Card className={cn(selected && 'border-primary/50 bg-primary/5')}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={onOpenActivity}
            className="flex items-center gap-2 min-w-0 text-left flex-1 group"
            title={row.title}
          >
            {bulkMode && (
              <Checkbox
                checked={selected}
                onCheckedChange={onToggleSelect}
                disabled={!isActivityActionable(row.status)}
                aria-label="Selecionar atividade"
                className={!isActivityActionable(row.status) ? 'opacity-40 cursor-not-allowed' : undefined}
              />
            )}
            <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <h3 className="text-sm font-medium truncate group-hover:text-primary group-hover:underline">
              {row.title}
            </h3>
          </button>
          <StatusBadge status={row.status} />
        </div>

        {row.deal_id && (
          <button
            type="button"
            onClick={onOpenDeal}
            className="text-left w-full group"
            title={`${row.deal_title ?? ''} · ${dealCode ?? ''}`}
          >
            <div className="text-sm font-medium text-primary group-hover:underline truncate">
              {row.deal_title ?? dealCode}
            </div>
            {valueLabel && (
              <div className="text-[11px] text-muted-foreground">{valueLabel}</div>
            )}
          </button>
        )}

        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          {row.customer_name && <span>{row.customer_name}</span>}
          {row.customer_phone && <span>· {formatPhoneForDisplay(row.customer_phone)}</span>}
          <DealStatusBadge status={row.deal_status} />
          <span>· {dueLabel}</span>
          {deadline && (
            <span
              className={cn(
                'inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                DEADLINE_PILL_STYLES[deadline.variant],
              )}
            >
              {deadline.text}
            </span>
          )}
        </div>

        {(row.workspace_name || row.pipeline_name) && (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground truncate">
            {row.workspace_name && (
              <span className="inline-flex items-center gap-1 truncate">
                <Building2 className="h-3 w-3 shrink-0" />
                {row.workspace_name}
              </span>
            )}
            {row.workspace_name && row.pipeline_name && <span>·</span>}
            {row.pipeline_name && (
              <span className="inline-flex items-center gap-1 truncate">
                <Layers className="h-3 w-3 shrink-0" />
                {row.pipeline_name}
              </span>
            )}
          </div>
        )}

        {/* v2.2.0 — Datas de criação/conclusão no card mobile */}
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
          {row.created_at && (
            <span>Criada: {format(new Date(row.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })}</span>
          )}
          {row.completed_at && (
            <span>· Concluída: {format(new Date(row.completed_at), 'dd/MM/yy HH:mm', { locale: ptBR })}</span>
          )}
        </div>


        <div className="flex items-center justify-between">
          <OriginCell row={row} />
          {(row.assignee_id || row.assignee_name) && (
            <div className="flex items-center gap-2">
              <SmartAvatar
                src={row.assignee_avatar ?? undefined}
                name={row.assignee_name ?? '—'}
                size="xs"
                use3DFallback
              />
              <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                {row.assignee_name ?? '—'}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Auxiliares: badge de status, célula de origem
// =============================================================================
export function StatusBadge({ status }: { status: ActivityStatus }) {
  const map: Record<ActivityStatus, { label: string; Icon: typeof CircleDot; cls: string }> = {
    pending: { label: 'Pendente', Icon: CircleDot, cls: 'bg-blue-500 text-white' },
    in_progress: { label: 'Em curso', Icon: CircleDot, cls: 'bg-amber-500 text-white' },
    overdue: { label: 'Atrasada', Icon: AlertTriangle, cls: 'bg-destructive text-destructive-foreground' },
    completed: { label: 'Concluída', Icon: CheckCircle2, cls: 'bg-emerald-600 text-white' },
    skipped: { label: 'Pulada', Icon: SkipForward, cls: 'bg-muted text-muted-foreground' },
  };
  const cfg = map[status] ?? map.pending;
  const I = cfg.Icon;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium', cfg.cls)}>
      <I className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

export function OriginCell({ row }: { row: ActivityRow }) {
  if (row.origin === 'cadence') {
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground min-w-0">
        <Zap className="h-3 w-3 text-purple-600 flex-shrink-0" />
        <span className="truncate" title={row.playbook_name ?? ''}>
          {row.playbook_name ?? 'Cadência'}
          {row.step_sort_order != null && ` · #${row.step_sort_order}`}
        </span>
      </div>
    );
  }
  // v1.4.0: agendamento online
  if (row.origin === 'booking') {
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground min-w-0">
        <CalendarCheck className="h-3 w-3 text-sky-600 flex-shrink-0" />
        <span className="truncate" title="Agendamento online">
          Agendamento online
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      <Briefcase className="h-3 w-3 flex-shrink-0" />
      <span>Avulsa</span>
    </div>
  );
}

// =============================================================================
// Skeleton + EmptyState
// =============================================================================
export function ActivitiesSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

export function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <ListChecks className="h-10 w-10 text-muted-foreground mb-3" />
      <h3 className="font-semibold mb-1">Nenhuma atividade encontrada</h3>
      <p className="text-sm text-muted-foreground max-w-md">
        Ajuste os filtros ou o período para ver atividades pendentes, atrasadas
        ou concluídas.
      </p>
    </div>
  );
}
